import React, { useEffect, useState } from 'react'
import { graphql, Link } from 'gatsby'
import Layout from '../components/pageComponents/layout'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { extract } from '../utils/pageBuilding'
import { useCategoriesI18n } from '../hooks/i18n/useCategoriesI18n'
import useSeoHrefLang from '../hooks/useSeoHrefLang'
import { getI18nText } from '../services/i18nService'
import PageRow from '../components/widgets/pageRow'
import VideoModal from '../components/modal/videoModal'
import Seo from '../components/pageComponents/seo'
import { extractTextValue } from '../services/htmlEncodingService'
import HomepageCarousel from '../components/homepage/homepageCarousel'
import { buildUrl } from '../services/urlService'
import { BrightEdgeAutoPilotHead } from '../components/pageComponents/brightEdgeAutoPilot'
import { setFontWeightClassNames } from '../services/forceFontWeightService'

const HomePage = (props) => {
  const categoryMicrocopy = useCategoriesI18n(props.pageContext.locale)
  const [data] = useState(extract(props.data.home, props.pageContext.id, props.pageContext.locale))
  const [videoModalIsOpen, setVideoModalIsOpen] = useState(false)
  const [videoLinkToPlay, setVideoLinkToPlay] = useState(null)

  const openVideoModal = (videoLink) => {
    setVideoLinkToPlay(videoLink)
    setVideoModalIsOpen(true)
  }

  const closeVideoModal = () => {
    setVideoModalIsOpen(false)
  }

  return (
    <Layout
      locale={props.pageContext.locale}
      altPaths={props.pageContext.altPaths}
      categoryPaths={props.pageContext.categoryPaths}
      darkTheme={false}
      id={props.pageContext.navId}
      title={data.pageTitle}
    >
      <HomepageCarousel
        headerImages={data.headerImages}
        openVideoModalCallback={openVideoModal}
        locale={props.pageContext.locale}
      ></HomepageCarousel>

      <section className="grid-lister text-light text-light-stone grid-col-3 is-industries">
        <div className="container">
          <div className="row">
            {data.expertise.map((vertical, verticalIndex) => {
              return (
                <Vertical
                  key={`vertical-${verticalIndex}`}
                  vertical={vertical}
                  categoryMicrocopy={categoryMicrocopy}
                />
              )
            })}
          </div>
        </div>
      </section>

      {data.rows.map((row, rowIndex) => {
        if (row.internal) {
          return (
            <PageRow
              key={rowIndex}
              locale={props.pageContext.locale}
              row={row}
              index={rowIndex}
              featuredBlogs={props.data.featuredBlogs.nodes}
            />
          )
        } else {
          return null
        }
      })}

      <VideoModal
        videoUrl={videoLinkToPlay}
        isOpen={videoModalIsOpen}
        closeVideoModalCallback={closeVideoModal}
      ></VideoModal>
    </Layout>
  )
}

export default HomePage

export function Head(props) {
  const seoHrefLang = useSeoHrefLang({
    locale: props.pageContext.locale,
    altPaths: props.pageContext.altPaths,
  })
  const [pageTitle] = useState(GetPageTitle())
  const [pageDescription] = useState(extractTextValue(props.pageContext.metaDescription))

  function GetPageTitle() {
    const data = extract(props.data.home, props.pageContext.id, props.pageContext.locale)
    let myPageTitle = extractTextValue(data.pageTitle)

    if (props.pageContext.titleOverride !== null && props.pageContext.titleOverride !== undefined) {
      myPageTitle = extractTextValue(props.pageContext.titleOverride)
    }

    return myPageTitle
  }

  return (
    <>
      {seoHrefLang}
      <Seo description={pageDescription} title={pageTitle} showTitleSuffix={false} />
      <BrightEdgeAutoPilotHead />
    </>
  )
}

const Vertical = ({ vertical, categoryMicrocopy }) => {
  const [titleClassNames, setTitleClassNames] = useState('force-font__light')
  const [image, setImage] = useState(null)

  useEffect(() => {
    setFontWeightClassNames(vertical.titleFontWeight, setTitleClassNames)

    if (vertical.image !== null && vertical.image !== undefined) {
      setImage(getImage(vertical.image))
    }
  }, [vertical])

  return (
    <div className="grid-lister__item col">
      <Link
        to={vertical.page ? buildUrl(vertical.page, '') : '/'}
        className="d-block position-relative"
      >
        <div className="grid-lister__image image__gradient image__overlay">
          <>
            {image ? (
              <GatsbyImage
                className="image__fit--cover no-opacity"
                image={image}
                alt={vertical.title}
              ></GatsbyImage>
            ) : null}
          </>
        </div>
        <div className="grid-lister__text position-absolute top-0 bottom-0 d-flex flex-column align-items-start">
          <h3 className={`mb-3 ${titleClassNames}`}>{vertical.title}</h3>
          <div className="grid-lister__excerpt flex-grow-1">
            <p>{vertical.description.description}</p>
          </div>
          <span className="btn has-arrow text-uppercase">
            {vertical.buttonText !== null && vertical.buttonText !== ''
              ? vertical.buttonText
              : getI18nText(categoryMicrocopy, 'categories.discover')}
          </span>
        </div>
      </Link>
    </div>
  )
}

export const pageQuery = graphql`
  query ($locale: String, $id: String) {
    home: allContentfulHome(filter: { node_locale: { eq: $locale }, id: { eq: $id } }) {
      nodes {
        id
        pageTitle
        headerImages {
          applyTint
          preTitleImage {
            gatsbyImageData(placeholder: BLURRED)
          }
          title
          subtitleFontWeight
          subtitle
          detailedText {
            detailedText
          }
          displayImiLogo
          navigationButtonText
          navigateTo {
            ... on ContentfulNavigationElement {
              navigation_element {
                slug
                navigation_element {
                  slug
                  navigation_element {
                    slug
                    navigation_element {
                      slug
                      navigation_element {
                        slug
                      }
                    }
                  }
                }
              }
              slug
            }
          }
          image {
            gatsbyImageData(placeholder: BLURRED)
          }
          vimeoVideo {
            name
            vimeoLink
            buttonText
          }
        }
        headline
        subheading
        intro {
          intro
        }
        rows {
          ... on ContentfulRow {
            id
            titleFontWeight
            title
            pageBlocks {
              id
              titleFontWeight
              title
              description {
                description
              }
              buttonText
              image {
                gatsbyImageData(placeholder: BLURRED, width: 600, height: 300)
              }
              url
              page {
                ... on ContentfulNavigationElement {
                  navigation_element {
                    slug
                    navigation_element {
                      slug
                      navigation_element {
                        slug
                        navigation_element {
                          slug
                          navigation_element {
                            slug
                          }
                        }
                      }
                    }
                  }
                  slug
                }
              }
            }
            internal {
              type
            }
            colour
            numberOfItems
            textColour
          }
          ... on ContentfulBlogRow {
            id
            colour
            numberOfItems
            textColour
            titleFontWeight
            title
            blogTitleFontWeight
            internal {
              type
            }
          }
        }
        expertise {
          titleFontWeight
          title
          description {
            description
          }
          buttonText
          image {
            gatsbyImageData(placeholder: BLURRED, width: 800, height: 400)
          }
          page {
            ... on ContentfulNavigationElement {
              navigation_element {
                slug
                navigation_element {
                  slug
                  navigation_element {
                    slug
                    navigation_element {
                      slug
                      navigation_element {
                        slug
                      }
                    }
                  }
                }
              }
              slug
            }
          }
        }
        node_locale
      }
    }
    featuredBlogs: allContentfulBlog(
      filter: { node_locale: { eq: $locale }, title: { ne: null }, feature: { eq: true } }
      sort: { date: DESC }
      limit: 4
    ) {
      nodes {
        ...FullBlogFields
      }
    }
  }
`
